import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, XCircle, RefreshCw, Archive, Download, ExternalLink, Save } from "lucide-react";

const STATUS = {
  active: { label: "Active", cls: "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/40", Icon: ShieldCheck },
  warning: { label: "Warning", cls: "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/40", Icon: AlertTriangle },
  error: { label: "Error", cls: "bg-[#EF4444]/15 text-[#f87171] border-[#EF4444]/40", Icon: XCircle },
};

export default function CitationsManager() {
  const [rows, setRows] = useState([]);
  const [checking, setChecking] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/citations").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runCheck = async () => {
    setChecking(true);
    try {
      const r = await api.post("/admin/citations/check");
      toast.success(`Status check complete — ${r.data.alerts.length} issue(s) found.`);
      load();
    } catch (e) { toast.error("Check failed"); } finally { setChecking(false); }
  };

  const archiveNow = async (d) => {
    setBusyId(d.id);
    try {
      await api.post(`/admin/citations/${d.id}/archive`);
      toast.success("Fresh archive snapshot requested.");
      load();
    } catch (e) { toast.error("Archive failed"); } finally { setBusyId(null); }
  };

  const saveUrl = async (d) => {
    try {
      await api.patch(`/admin/citations/${d.id}`, { original_url: editUrl });
      toast.success("URL updated.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("token");
    const url = `${api.defaults.baseURL}/admin/citations/export`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "worldaiinfo_citations.csv";
        a.click();
      }).catch(() => toast.error("Export failed"));
  };

  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="citations-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <p className="font-mono-tech text-xs text-[#94A3B8]">
          {rows.length} citations · <span className="text-[#4ade80]">{counts.active || 0} active</span>
          {counts.warning ? <> · <span className="text-[#fbbf24]">{counts.warning} warning</span></> : null}
          {counts.error ? <> · <span className="text-[#f87171]">{counts.error} error</span></> : null}
        </p>
        <div className="flex gap-2">
          <button onClick={runCheck} disabled={checking} data-testid="citations-check-btn"
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-white/15 hover:border-[#00F0FF]/40 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} /> Run status check
          </button>
          <button onClick={exportCsv} data-testid="citations-export-btn"
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-[#00F0FF] text-[#04121a] font-semibold hover:brightness-110">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((d) => {
          const st = STATUS[d.status] || STATUS.active;
          return (
            <div key={d.id} className="card-surface rounded-lg p-4" data-testid={`citation-row-${d.key}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-sm">{d.statistic || d.key}</span>
                    <span className={`inline-flex items-center gap-1 font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded-full border ${st.cls}`}>
                      <st.Icon className="w-3 h-3" /> {st.label}
                    </span>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-1">{d.organisation}{d.report_name ? ` — ${d.report_name}` : ""}{d.date ? ` — ${d.date}` : ""}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-[11px]">
                    <a href={d.original_url} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline inline-flex items-center gap-1">Original <ExternalLink className="w-3 h-3" /></a>
                    {d.archive_url && <a href={d.archive_url} target="_blank" rel="noreferrer" className="text-[#94A3B8] hover:text-[#00F0FF] inline-flex items-center gap-1">Archive <ExternalLink className="w-3 h-3" /></a>}
                    <span className="text-[#64748B]">Verified: {d.last_verified || "—"}</span>
                  </div>
                  {editing === d.id && (
                    <div className="flex gap-2 mt-2">
                      <input value={editUrl} onChange={(e) => setEditUrl(e.target.value)} data-testid="citation-url-input"
                        className="flex-1 bg-[#0B0F19] border border-white/10 rounded px-2 py-1 text-xs focus:border-[#00F0FF] outline-none" />
                      <button onClick={() => saveUrl(d)} data-testid={`citation-save-${d.key}`} className="text-xs bg-[#00F0FF] text-[#04121a] font-semibold px-2.5 py-1 rounded flex items-center gap-1"><Save className="w-3 h-3" /> Save</button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => { setEditing(d.id); setEditUrl(d.original_url); }} data-testid={`citation-edit-${d.key}`}
                    className="text-xs px-2.5 py-1.5 rounded border border-white/15 hover:border-[#00F0FF]/40 text-[#94A3B8]">Update URL</button>
                  <button onClick={() => archiveNow(d)} disabled={busyId === d.id} data-testid={`citation-archive-${d.key}`}
                    className="text-xs px-2.5 py-1.5 rounded border border-white/15 hover:border-[#00F0FF]/40 text-[#94A3B8] inline-flex items-center gap-1 disabled:opacity-50">
                    <Archive className="w-3.5 h-3.5" /> Archive Now
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
